/** @jsx CanvaSX */
import { CanvaSX, type CanvaSXObject } from '@lib/canvasx/canvasx';
import { Rect } from '@lib/canvasx/components/Rect';
import { type TextProps, Text } from '@lib/canvasx/components/Text';
import { normalizeSpacing } from '@lib/canvasx/shapes';
import { fontForProps, parseFont } from '@lib/canvasx/textRendering';
import { calculateDimensionsForProps } from '@lib/canvasx/util';
import { pick } from 'lodash';

const MIN_WIDTH = 10;

const setFabricListeners = (
  object: fabric.Object & CanvaSXObject,
  input: HTMLInputElement,
  span: HTMLSpanElement,
  key: string,
  add = true
) => {
  const listener = add ? 'on' : 'off';
  if (object) {
    object[listener]?.('moving', () => {
      endTextEditing(object, input, span, key);
    });
    object.canvas?.[listener]?.('pan', () => {
      endTextEditing(object, input, span, key);
    });
    object.canvas?.[listener]?.('zoom', () => {
      endTextEditing(object, input, span, key);
    });
  }
};

const endTextEditing = (
  object: fabric.Object & CanvaSXObject,
  input: HTMLInputElement,
  span: HTMLSpanElement,
  key: string
) => {
  setTimeout(() => {
    if (document.contains(input)) {
      input.remove();
      span.remove();
      setFabricListeners(object, input, span, key, false);
      object.set('textEditing', false);
      object.canvas?.requestRenderAll();
    }
  });
};

const initElement = (element, style, fontFamily, container: HTMLDivElement) => {
  Object.assign(element.style, style);
  element.style.setProperty('font-family', fontFamily, 'important');
  container.append(element);
};

const getMountedObjectCoords = (
  object: fabric.Object & CanvaSXObject,
  key: string
): { left: number; top: number; width: number; height: number } => {
  const mountedObject = object.mountedObjects[key];
  const { left: objectLeft, top: objectTop } = object;
  const { x, y, width, height } = mountedObject;
  const { left, top } = object.canvas.rectToWindowCoords({
    left: objectLeft + x,
    top: objectTop + y,
    width,
    height,
  });

  return { left, top, width, height };
};

const onClick = (
  ctx: CanvasRenderingContext2D,
  object: fabric.Object & CanvaSXObject,
  props: ElementProps<EditableTextProps>
) => {
  const {
    textKey: key,
    onChange,
    color,
    children,
    minWidth = 50,
    opacity = 1,
  } = props;
  if (!key) {
    console.error('Invalid key for EditableText');
    return;
  }

  // Tell the object that field is being edited
  object.textEditing = true;
  const zoom = object.canvas?.getZoom() ?? 1;

  // Get coords to line input element up with text
  const { scaleX, scaleY } = object;
  const {
    left,
    top,
    height: componentHeight,
  } = getMountedObjectCoords(object, key);
  const scale = zoom * scaleX;

  // Normalize the font.
  const { fontSize, fontFamily, fontWeight } = parseFont(fontForProps(props));

  let size = props.size;
  size ||= fontSize;
  size *= scale;

  // Set input element styling to show just text and cursor
  const style = {
    margin: '0px',
    padding: '0px',
    position: 'absolute',
    left: left.toString() + 'px',
    top: (top - scaleY * zoom).toString() + 'px',
    height: (componentHeight * zoom).toString() + 'px',
    color,
    fontSize: size.toString() + 'px',
    background: 'transparent',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
    maxWidth: `calc(100% - ${left}px)`,
    overflow: 'hidden',
    textWrap: 'nowrap',
    fontWeight,
    opacity,
  };
  const mirrorStyle = {
    ...style,
    position: 'absolute',
    visibility: 'hidden',
    whiteSpace: 'pre',
    padding: '0px',
  };
  const container = object.controller.canvasxInput!;
  const mirror = document.createElement('span');
  const input = document.createElement('input');
  initElement(input, style, fontFamily, container);
  initElement(mirror, mirrorStyle, fontFamily, container);

  // Set the value to the current text
  if (!Array.isArray(children) || typeof children[0] !== 'string') {
    throw new Error('Invalid child type for editable text');
  } else {
    input.value = children[0];
  }

  const updatePositionDimension = () => {
    // keep a min width in case the user deletes all text
    mirror.textContent = input.value || ' ';
    onChange?.(input.value);
    requestAnimationFrame(() => {
      // Nesting a second animation frame guarantees we will run after the outer
      // requestAnimationFrame-related paint finishes. This is necessary for getting
      // updated position for mounted objects after the updates of this input
      // change have taken affect
      requestAnimationFrame(() => {
        input.style.width = `${Math.max(mirror.offsetWidth, minWidth)}px`;
        const { left } = getMountedObjectCoords(object, key);
        input.style.left = left.toString() + 'px';
      });
    });
  };

  // Init mirror content
  updatePositionDimension();
  // Start text editing
  input.focus();

  // On input, update the parent component's value
  input.addEventListener('input', updatePositionDimension);

  // Remove the editable field when blurred
  input.addEventListener('blur', () =>
    endTextEditing(object, input, mirror, key)
  );

  // Init fabric listeners to end text editing (e.g., pan, zoom, etc.)
  setFabricListeners(object, input, mirror, key, true);

  object.canvas.requestRenderAll();
};

export type EditableTextProps = {
  textKey?: string;
  textEditing?: boolean;
  onChange?: (value: string) => void;
  minWidth?: number;
} & Partial<FontStruct> &
  Partial<ShapeStruct> &
  Partial<SpacingStruct> &
  TextProps;

export const EditableText: CanvaSXFC<EditableTextProps> = function (props) {
  const {
    children,
    color,
    textKey,
    padding,
    textEditing,
    border,
    fill,
    minWidth = MIN_WIDTH,
    width = 0,
    height,
    verticalAlignment = 'default',
  } = props;
  const textProps = pick(
    props,
    'x',
    'y',
    'children',
    'color',
    'maxWidth',
    'overflow',
    'margin',
    'opacity',
    'bold',
    'size'
  );

  const { ctx, object } = CanvaSX.context;

  const spacing = normalizeSpacing(padding);
  const horizontalPadding = spacing.left + spacing.right;
  const verticalPadding = spacing.bottom + spacing.top;

  // We need to tell the inner `<Rect>` what its dimensions are otherwise
  // it will wrap around the `<Text>`. This is fine when at the default size
  // where <Text> fills the entire space. If the parent component is
  // expanded vertically and the <Rect> doesn't also grow we get a dead zone
  // above and below the text where the onClick handler doesn't fire.
  // And the blue "editing" outline won't fill the entire space either.
  let computedHeight = height ?? 0;
  let computedWidth = width ?? 0;

  computedHeight = Math.max(0, computedHeight - verticalPadding);
  computedWidth = Math.max(0, computedWidth - horizontalPadding);

  return (
    <Rect border={border} fill={fill} padding={padding}>
      <Rect
        key={textKey}
        cursor='text'
        onClick={() => {
          onClick(ctx, object as fabric.Object & CanvaSXObject, props);
        }}
        width={Math.max(computedWidth, minWidth)}
        height={computedHeight !== height ? computedHeight : undefined}
      >
        <Text
          {...textProps}
          verticalAlignment={verticalAlignment}
          height={computedHeight !== height ? computedHeight : undefined}
          width={computedWidth}
          color={textEditing ? 'transparent' : color}
          setRenderedDimensions={dimensions => {
            // Add any padding to the dimensions of the text to reflect
            // the visible dimensions of the EditableText component
            if (props.setRenderedDimensions) {
              props.setRenderedDimensions({
                height: dimensions.height + verticalPadding,
                width: dimensions.width + horizontalPadding,
              });
            }
          }}
        >
          {children}
        </Text>
      </Rect>
    </Rect>
  );
};

EditableText.calculateDimensions = props => {
  const { minWidth = MIN_WIDTH } = props;

  // Need to set font for measuring
  const { ctx } = CanvaSX.context;

  ctx.save();
  ctx.font = fontForProps(props);

  const { width, height } = calculateDimensionsForProps(props);

  ctx.restore();

  return { width: Math.max(width, minWidth), height };
};
